/**
 * Diligence Report Viewer
 * =======================
 * The finished diligence report, rendered inside the lead's Due Diligence tab.
 *
 * A run that finished and stored a PDF is the one document an operator opens
 * the lead to read. It costs USD 8 to 12 and takes about 27 minutes. So it is
 * shown in the tab itself, in an `<iframe>`, rather than behind a button.
 *
 * A lead can hold several report versions. `run` is the version the operator
 * SELECTED, not the lead's newest one. The badge, the finished date, Download
 * and Open in new tab all follow that selection.
 *
 * `versions` holds every version that has a report, newest first, and fills a
 * dropdown beside the badge. A lead with one version hides the dropdown and
 * looks exactly as it did before versioning. A dropdown was chosen over tabs
 * or arrows because the frame needs the height: a dropdown costs one line
 * whatever the number of versions.
 *
 * The parent renders this viewer only when the lead's newest version holds a
 * report. So `versions[0]` is that newest version, and the notice above the
 * frame can name it.
 *
 * The URL is presigned and lives one hour. Three things follow from that:
 *
 *   - The frame is keyed on the URL. A new URL is a new key, so React
 *     remounts the frame instead of leaving a stale document on screen.
 *   - A timer re-signs every 50 minutes, ten minutes inside the hour.
 *   - Reload is offered anyway, because a laptop that slept through the timer
 *     wakes up holding a dead URL and no timer can help it.
 *
 * Open in new tab is the parent's `handleOpenReport`. It opens the blank tab
 * synchronously inside the click and navigates it after the fresh read
 * resolves. A `window.open` called after an `await` is blocked as a popup.
 *
 * The frame asks the browser's PDF viewer to hide its own toolbar and
 * thumbnail sidebar. Both duplicate actions this card already offers, and
 * inside a frame this narrow they eat the space the report needs. The
 * standalone tab and the download keep the full viewer.
 */

import React, { useEffect, useRef } from 'react';
import { Card, CardBody, Badge, Button } from '../ui';

// Ten minutes inside the hour a presigned URL lives. Long enough that an
// operator reading a report is not interrupted, short enough that the frame
// never holds a dead URL while the tab is in the foreground.
const RESIGN_INTERVAL_MS = 50 * 60 * 1000;

// PDF open parameters, read by the browser's built-in viewer from the URL
// fragment. `toolbar=0` drops the grey bar, `navpanes=0` the thumbnail
// sidebar, `scrollbar=0` the viewer's own scrollbar. `view=FitH` then fits
// the page to the frame width so the reclaimed space goes to the report.
const VIEWER_PARAMS = 'toolbar=0&navpanes=0&scrollbar=0&view=FitH';

// The presigned URL carries its signature in the query string, so the
// parameters go in the fragment and leave the signature untouched. A URL that
// already has a fragment keeps it; the parameters are appended to it.
const withViewerChrome = (url) => {
  if (!url) return url;
  return url.includes('#') ? `${url}&${VIEWER_PARAMS}` : `${url}#${VIEWER_PARAMS}`;
};

const formatMoment = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString();
};

// Shorter than `formatMoment`, because a dropdown option carries the version
// number as well as the date and a full timestamp would not fit one line.
const formatOptionMoment = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// When a version finished. `completed_at` is the run's own end; a version
// whose row was written before that field existed falls back to the moment its
// document was stored.
const versionMoment = (version) =>
  formatOptionMoment(version?.completed_at || version?.pdf_generated_at);

const optionLabel = (version, isNewest) => {
  const moment = versionMoment(version);
  const head = moment ? `Version ${version.version} — ${moment}` : `Version ${version.version}`;
  return isNewest ? `${head} (latest)` : head;
};

const DiligenceReportViewer = ({
  run,
  versions = [],
  selectedVersion = null,
  onSelectVersion,
  reportUrl,
  onResign,
  onReload,
  onOpenInNewTab,
  reloading = false,
  opening = false,
  runAgainButton = null,
}) => {
  const gateFailed = run?.status === 'completed_gate_failed';
  const finishedMoment = formatMoment(run?.completed_at || run?.pdf_generated_at);

  // One version needs no switcher, and a lead with one version then sees
  // exactly what it saw before versioning.
  const showSwitcher = versions.length > 1 && Boolean(onSelectVersion);

  // `versions` arrives newest first, so the first entry is the lead's newest
  // version. The notice below compares the selection against it.
  const newestVersion = versions.length > 0 ? versions[0] : null;
  const viewingOlder =
    newestVersion !== null &&
    selectedVersion !== null &&
    selectedVersion !== newestVersion.version;

  // Re-sign on a timer. The callback is held in a ref so a new function
  // identity on each parent render does not restart the 50-minute clock.
  const resignRef = useRef(onResign);
  resignRef.current = onResign;

  useEffect(() => {
    if (!onResign) return undefined;
    const intervalId = setInterval(() => {
      if (resignRef.current) resignRef.current();
    }, RESIGN_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [onResign]);

  return (
    <Card>
      <CardBody className="space-y-4">
        {/* Status strip */}
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant={gateFailed ? 'warning' : 'success'}>
                {gateFailed ? 'Held for review' : 'Completed'}
              </Badge>
              {showSwitcher && (
                <select
                  aria-label="Report version"
                  value={selectedVersion ?? ''}
                  onChange={(e) => onSelectVersion(Number(e.target.value))}
                  className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 dark:border-[#3A3C3E] dark:bg-[#2A2C2E] dark:text-white"
                >
                  {versions.map((version, index) => (
                    <option key={version.version} value={version.version}>
                      {optionLabel(version, index === 0)}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <h3 className="mt-2 text-xl font-sans font-semibold text-gray-900 dark:text-white">
              Due diligence report
            </h3>
            {finishedMoment && (
              <p className="mt-0.5 text-sm text-gray-500 dark:text-[#A0A4A8]">
                Finished {finishedMoment}
              </p>
            )}
          </div>

          {/* The four actions. Reload and Open in new tab both re-sign, so
              neither can hand the operator an expired URL. */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              onClick={onReload}
              loading={reloading}
            >
              Reload
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              onClick={onOpenInNewTab}
              loading={opening}
            >
              Open in new tab
            </Button>
            {reportUrl && (
              <a
                href={reportUrl}
                download
                className="btn btn-sm btn-outline-primary"
              >
                Download
              </a>
            )}
            {runAgainButton}
          </div>
        </div>

        {/* A gate-failed run still shows its report. The banner says why it is
            held, so nobody forwards it to an investment committee unread. */}
        {gateFailed && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-700 dark:bg-amber-900/20">
            <p className="font-medium text-amber-800 dark:text-amber-300">
              This report did not pass the final quality gate. It is held for review.
            </p>
            {run?.error && (
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">{run.error}</p>
            )}
          </div>
        )}

        {/* The selection is not the lead's newest version. Said plainly, so
            nobody circulates a superseded report believing it is current. */}
        {viewingOlder && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-sm text-gray-700 dark:text-[#A0A4A8]">
              You are viewing version {selectedVersion}. Version{' '}
              {newestVersion.version} is the newest.
            </p>
          </div>
        )}

        {reportUrl ? (
          <iframe
            // Keyed on the URL so a re-signed URL remounts the frame. Without
            // the key React reuses the element and some browsers keep showing
            // the document loaded from the old, now-dead URL.
            key={reportUrl}
            src={withViewerChrome(reportUrl)}
            title="Due diligence report"
            className="h-[calc(100vh-320px)] min-h-[600px] w-full rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900"
          />
        ) : (
          <div className="rounded-lg border border-gray-200 px-4 py-12 text-center dark:border-gray-700">
            <p className="text-gray-600 dark:text-[#A0A4A8]">
              The report link could not be signed. Press Reload to try again.
            </p>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default DiligenceReportViewer;
